import React, {useState} from "react";
import {toast} from "react-toastify";
import Button from "../../../components/Button";
import AdminTable, {AdminTableColumn} from "../components/AdminTable";
import ConfirmDialog from "../components/ConfirmDialog";
import UserForm from "./UserForm";
import {AdminUser} from "../../../interfaces/admin";
import {
    useGetAdminUsersQuery,
    useCreateAdminUserMutation,
    useUpdateAdminUserMutation,
    useDeleteAdminUserMutation,
} from "../../../storage/adminApi";
import localStyles from "../styles.module.scss";

const AdminUsers = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sortBy, setSortBy] = useState("id");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [showForm, setShowForm] = useState(false);
    const [editUser, setEditUser] = useState<AdminUser | undefined>(undefined);
    const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);

    const {data, isLoading} = useGetAdminUsersQuery({page, per_page: pageSize, sort_by: sortBy, sort_order: sortOrder});
    const [createUser] = useCreateAdminUserMutation();
    const [updateUser] = useUpdateAdminUserMutation();
    const [deleteUser] = useDeleteAdminUserMutation();

    const columns: AdminTableColumn[] = [
        {label: "ID", key: "id", sortable: true},
        {label: "Username", key: "username"},
        {
            label: "Admin",
            key: "is_admin",
            render: (value: boolean) =>
                value
                    ? <span className={`${localStyles.badge} ${localStyles.badgeSuccess}`}>Admin</span>
                    : <span className={`${localStyles.badge} ${localStyles.badgeSecondary}`}>User</span>,
        },
        {label: "Created", key: "created_at", sortable: true},
        {
            label: "Actions",
            key: "actions",
            render: (_: any, row: AdminUser) => (
                <div className={localStyles.actionsCell}>
                    <Button
                        id={`edit-user-${row.id}`}
                        size="small"
                        background="bg-outline"
                        text="Edit"
                        click={(e) => {
                            e.stopPropagation();
                            setEditUser(row);
                            setShowForm(true);
                        }}
                    />
                    <Button
                        id={`delete-user-${row.id}`}
                        size="small"
                        background="bg-danger"
                        text="Delete"
                        click={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(row);
                        }}
                    />
                </div>
            ),
        },
    ];

    const handleSubmit = async (values: {username: string; password: string; is_admin: boolean}) => {
        try {
            if (editUser) {
                const updateData: any = {username: values.username, is_admin: values.is_admin};
                if (values.password) {
                    updateData.password = values.password;
                }
                await updateUser({userId: editUser.id, data: updateData}).unwrap();
                toast.success("User updated");
            } else {
                await createUser(values).unwrap();
                toast.success("User created");
            }
            setShowForm(false);
            setEditUser(undefined);
        } catch (e) {
            toast.error("Operation failed");
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteUser(deleteTarget.id).unwrap();
            toast.success("User deleted");
            setDeleteTarget(null);
        } catch (e) {
            toast.error("Failed to delete user");
        }
    };

    return (
        <div>
            <div className={localStyles.pageHeader}>
                <h4>Users</h4>
                <Button
                    id="create-user"
                    size="small"
                    background="bg-primary"
                    text="Create User"
                    click={() => {
                        setEditUser(undefined);
                        setShowForm(true);
                    }}
                />
            </div>

            <AdminTable
                columns={columns}
                data={data?.items ?? []}
                total={data?.total ?? 0}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                }}
                loading={isLoading}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={(newSortBy, newSortOrder) => {
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                    setPage(1);
                }}
            />

            {showForm && (
                <UserForm
                    user={editUser}
                    onSubmit={handleSubmit}
                    onCancel={() => {
                        setShowForm(false);
                        setEditUser(undefined);
                    }}
                />
            )}

            <ConfirmDialog
                show={!!deleteTarget}
                title="Delete User"
                message={`Are you sure you want to delete user "${deleteTarget?.username}"?`}
                onConfirm={handleDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
};

export default <AdminUsers/>;
